import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { MemberSidebar } from "@/components/layout/MemberSidebar";
import { MemberHeader } from "@/components/layout/MemberHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Loader2, BookOpen, ExternalLink, AlertTriangle, Sparkles, History, Trash2, Clock, Bookmark, BookmarkCheck } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { format } from "date-fns";

interface Resource {
  title: string;
  url: string;
  source: string;
}

type ViewMode = "search" | "history" | "bookmarks";

export default function HealthFaqSearch() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [answer, setAnswer] = useState("");
  const [currentSearchId, setCurrentSearchId] = useState<string | null>(null);
  const [currentBookmarked, setCurrentBookmarked] = useState(false);
  const [resources, setResources] = useState<Resource[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("search");

  const invalidateHistory = () => queryClient.invalidateQueries({ queryKey: ["health-faq-history", user?.id] });

  // Fetch search history
  const { data: history } = useQuery({
    queryKey: ["health-faq-history", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("health_faq_searches" as any)
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!user,
  });

  const bookmarkedItems = history?.filter((item: any) => item.is_bookmarked) || [];

  // Save search mutation
  const saveSearch = useMutation({
    mutationFn: async ({ query, answer, resources }: { query: string; answer: string; resources: Resource[] }) => {
      const { data, error } = await supabase.from("health_faq_searches" as any).insert({
        user_id: user?.id,
        query,
        answer,
        resources: JSON.stringify(resources),
      } as any).select("id").single();
      if (error) throw error;
      return data as any;
    },
    onSuccess: (data) => {
      setCurrentSearchId(data?.id || null);
      setCurrentBookmarked(false);
      invalidateHistory();
    },
  });

  // Delete search mutation
  const deleteSearch = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("health_faq_searches" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidateHistory,
  });

  // Toggle bookmark mutation
  const toggleBookmark = useMutation({
    mutationFn: async ({ id, bookmarked }: { id: string; bookmarked: boolean }) => {
      const { error } = await supabase
        .from("health_faq_searches" as any)
        .update({ is_bookmarked: bookmarked } as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidateHistory,
  });

  const runSearch = async (searchQuery: string) => {
    if (!searchQuery.trim() || isLoading) return;

    setIsLoading(true);
    setError("");
    setAnswer("");
    setResources([]);
    setHasSearched(true);
    setViewMode("search");
    setCurrentSearchId(null);
    setCurrentBookmarked(false);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("health-faq-search", {
        body: { query: searchQuery.trim() },
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      const answerText = data.answer || "";
      const resourceList = data.resources || [];
      setAnswer(answerText);
      setResources(resourceList);

      if (answerText) {
        saveSearch.mutate({ query: searchQuery.trim(), answer: answerText, resources: resourceList });
      }
    } catch (err: any) {
      console.error("Health FAQ search error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    runSearch(query);
  };

  const loadFromHistory = (item: any) => {
    setQuery(item.query);
    setAnswer(item.answer);
    setCurrentSearchId(item.id);
    setCurrentBookmarked(!!item.is_bookmarked);
    try {
      setResources(typeof item.resources === "string" ? JSON.parse(item.resources) : item.resources || []);
    } catch {
      setResources([]);
    }
    setHasSearched(true);
    setViewMode("search");
  };

  const handleToggleCurrentBookmark = () => {
    if (!currentSearchId) return;
    const newVal = !currentBookmarked;
    setCurrentBookmarked(newVal);
    toggleBookmark.mutate({ id: currentSearchId, bookmarked: newVal });
  };

  const suggestedQuestions = [
    "What is high blood pressure?",
    "How can I manage my diabetes?",
    "What are the symptoms of the flu?",
    "How much sleep do I need?",
    "What vaccines do adults need?",
  ];

  const renderHistoryList = (items: any[], emptyMessage: string) => (
    <Card className="mb-6">
      <CardContent className="pt-5">
        {items.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{emptyMessage}</p>
        ) : (
          <div className="space-y-2">
            {items.map((item: any) => (
              <div
                key={item.id}
                className="flex items-center justify-between rounded-lg border px-4 py-3 hover:bg-muted/50 transition-colors group cursor-pointer"
                onClick={() => loadFromHistory(item)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {item.is_bookmarked ? (
                    <BookmarkCheck className="h-3.5 w-3.5 text-primary shrink-0" />
                  ) : (
                    <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  )}
                  <span className="text-sm font-medium truncate">{item.query}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {format(new Date(item.created_at), "MMM d, h:mm a")}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleBookmark.mutate({ id: item.id, bookmarked: !item.is_bookmarked });
                    }}
                  >
                    {item.is_bookmarked ? (
                      <BookmarkCheck className="h-3 w-3 text-primary" />
                    ) : (
                      <Bookmark className="h-3 w-3 text-muted-foreground" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSearch.mutate(item.id);
                    }}
                  >
                    <Trash2 className="h-3 w-3 text-muted-foreground" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background">
      <MemberSidebar />
      <div className="pl-64">
        <MemberHeader />
        <main className="p-6 max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Health FAQ Search</h1>
                <p className="text-sm text-muted-foreground">
                  Ask any health question and get AI-powered educational answers
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {bookmarkedItems.length > 0 && (
                <Button
                  variant={viewMode === "bookmarks" ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => setViewMode(viewMode === "bookmarks" ? "search" : "bookmarks")}
                >
                  <BookmarkCheck className="h-4 w-4 mr-1.5" />
                  Saved ({bookmarkedItems.length})
                </Button>
              )}
              {history && history.length > 0 && (
                <Button
                  variant={viewMode === "history" ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => setViewMode(viewMode === "history" ? "search" : "history")}
                >
                  <History className="h-4 w-4 mr-1.5" />
                  History ({history.length})
                </Button>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Type your health question here..."
                  className="pl-10 h-12 text-base"
                  disabled={isLoading}
                />
              </div>
              <Button type="submit" disabled={!query.trim() || isLoading} className="h-12 px-6">
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
              </Button>
            </div>
          </form>

          {/* Bookmarks Panel */}
          {viewMode === "bookmarks" && renderHistoryList(bookmarkedItems, "No saved answers yet. Bookmark an answer to save it here.")}

          {/* Search History Panel */}
          {viewMode === "history" && history && renderHistoryList(history, "No search history yet.")}

          {/* Suggested Questions */}
          {!hasSearched && viewMode === "search" && (
            <div className="mb-8">
              <p className="text-sm font-medium text-muted-foreground mb-3">Try asking:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedQuestions.map((q) => (
                  <Button
                    key={q}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => {
                      setQuery(q);
                      runSearch(q);
                    }}
                  >
                    {q}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Disclaimer Banner */}
          <Card className="mb-6 border-amber-200 bg-amber-50/50 dark:bg-amber-950/10 dark:border-amber-800">
            <CardContent className="flex items-start gap-3 py-3 px-4">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800 dark:text-amber-300">
                This tool provides general health information for educational purposes only.
                It is <strong>not</strong> a substitute for professional medical advice, diagnosis, or treatment.
                Always consult your healthcare provider with any medical concerns.
              </p>
            </CardContent>
          </Card>

          {/* Error */}
          {error && (
            <Card className="mb-6 border-destructive/50 bg-destructive/5">
              <CardContent className="py-4 px-5">
                <p className="text-sm text-destructive">{error}</p>
              </CardContent>
            </Card>
          )}

          {/* Loading State */}
          {isLoading && (
            <Card className="mb-6">
              <CardContent className="py-12 flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Researching your question...</p>
              </CardContent>
            </Card>
          )}

          {/* Answer */}
          {answer && !isLoading && (
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <h2 className="font-semibold text-foreground">AI Answer</h2>
                  </div>
                  {currentSearchId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleToggleCurrentBookmark}
                      className="gap-1.5 text-xs"
                    >
                      {currentBookmarked ? (
                        <>
                          <BookmarkCheck className="h-4 w-4 text-primary" />
                          Saved
                        </>
                      ) : (
                        <>
                          <Bookmark className="h-4 w-4" />
                          Save
                        </>
                      )}
                    </Button>
                  )}
                </div>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{answer}</ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Resources */}
          {resources.length > 0 && !isLoading && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="h-4 w-4 text-primary" />
                  <h2 className="font-semibold text-foreground">Trusted Health Resources</h2>
                </div>
                <div className="space-y-3">
                  {resources.map((r, i) => (
                    <a
                      key={i}
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between rounded-lg border px-4 py-3 hover:bg-muted/50 transition-colors group"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <BookOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm font-medium truncate">{r.title}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="secondary" className="text-[10px]">{r.source}</Badge>
                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
}
